import { onBeforeUnmount, reactive } from 'vue'

export interface SpringPosition {
  current: number
  default: number
  target: number
}

export interface SpringSettings {
  dt?: number
  duration?: number // Duration in milliseconds
  friction?: number
  stiffness?: number
  threshold?: number
}

class Spring {
  private acceleration = 0
  private dt = 1 / 60 // Changed to 60fps for smoother animation
  private duration?: number
  private friction = 18
  private initialPosition = 0
  private isAnimating = false

  private position = {
    current: 0,
    default: 0,
    target: 0,
  }

  private startTime?: number
  private stiffness = 420
  private velocity = 0

  constructor(
    position: number,
    stiffness?: number,
    friction?: number,
    dt?: number,
    duration?: number,
  ) {
    this.setPosition(position)
    this.velocity = 0
    this.acceleration = 0

    if (duration) {
      // Calculate optimal spring parameters for the given duration
      // Using critically damped spring equation
      this.duration = duration
      const omega = 5.5 / (duration / 1000) // Natural frequency

      this.stiffness = omega * omega // k = ω²
      this.friction = 2 * Math.sqrt(this.stiffness) // c = 2√k
    }
    else {
      this.stiffness = stiffness || 420
      this.friction = friction || 40
    }

    this.dt = dt || 1 / 60
  }

  getDefault(): number {
    return this.position.default
  }

  getPosition(): number {
    return this.position.current
  }

  isComplete(): boolean {
    return !this.isAnimating
  }

  setPosition(position: number): void {
    this.position = [
      'target',
      'current',
      'default',
    ].reduce((memo, key) => {
      memo[key as keyof SpringPosition] = position

      return memo
    }, {} as SpringPosition)
  }

  setTarget(position?: number): void {
    if (position !== undefined) {
      this.initialPosition = this.position.current
      this.position.target = position
      if (this.duration) {
        this.startTime = performance.now()
      }
      this.isAnimating = true
    }
    else {
      this.position.target = this.position.default
    }
  }

  update(): void {
    const eps = 0.01

    if (this.duration && this.startTime) {
      const elapsed = performance.now() - this.startTime

      if (elapsed >= this.duration) {
        this.position.current = this.position.target
        this.velocity = 0
        this.acceleration = 0
        this.isAnimating = false

        return
      }

      // Use normalized time for smooth interpolation
      const t = Math.min(elapsed / this.duration, 1)

      // Calculate position using spring motion equation
      const omega = Math.sqrt(this.stiffness)
      const x0 = this.initialPosition - this.position.target
      const decay = Math.exp(-omega * t)

      this.position.current = this.position.target + x0 * (
        (1 + omega * t) * decay
      )

      // Calculate velocity for continuous motion
      this.velocity = -x0 * omega * omega * t * decay

      return
    }

    const smallerThanEps = [
      this.position.current - this.position.target,
      this.acceleration,
      this.velocity,
    ].reduce((memo, value) => memo && Math.abs(value) < eps, true)

    if (!smallerThanEps) {
      const dist = this.position.target - this.position.current

      this.acceleration = this.stiffness * dist - this.friction * this.velocity
      this.velocity += this.acceleration * this.dt
      this.position.current += this.velocity * this.dt
    }
    else {
      this.position.current = this.position.target
      this.velocity = 0
      this.acceleration = 0
      this.isAnimating = false
    }
  }
}

interface SpringState {
  [key: string]: number
}

interface SpringResult<T extends SpringState> {
  apply: (newValues: Partial<T>, onComplete?: () => void) => void
  set: (newValues: Partial<T>) => void
  values: T
}

export function useSpring<T extends SpringState>(
  initialState: MaybeRef<T>,
  springSettings: MaybeRef<SpringSettings> = {},
): SpringResult<T> {
  // Create reactive values object
  const values = reactive({ ...initialState }) as T

  // Create springs map to store spring instances
  let springs: Map<keyof T, Spring>

  // Initialize springs from initial state and spring settings
  watch(
    [() => unref(springSettings), () => unref(initialState)],
    ([settings, state]) => {
      springs = new Map<keyof T, Spring>()

      Object.entries(state).forEach(([key, value]) => {
        springs.set(
          key as keyof T,
          new Spring(
            value,
            settings.stiffness,
            settings.friction,
            settings.dt,
            settings.duration,
          ),
        )
      })
    },
    { immediate: true },
  )

  // Animation frame tracking
  let animationFrameId: null | number = null
  let currentCallback: (() => void) | undefined

  // Update function to be called each frame
  const update = () => {
    let isAnimating = false

    springs.forEach((spring, key) => {
      spring.update()
      const newPosition = spring.getPosition()

      if (values[key] !== newPosition) {
        values[key] = newPosition as T[keyof T]
        isAnimating = true
      }
    })

    if (isAnimating) {
      animationFrameId = requestAnimationFrame(update)
    }
    else {
      animationFrameId = null
      // Call onComplete callback when all springs have finished animating
      if (currentCallback) {
        currentCallback()
        currentCallback = undefined
      }
    }
  }

  // Function to start animation if not already running
  const startAnimation = () => {
    if (!animationFrameId) {
      animationFrameId = requestAnimationFrame(update)
    }
  }

  // Apply function - animate to new values
  const apply = (newValues: Partial<T>, onComplete?: () => void) => {
    // Skip rendering in SSR
    if (!import.meta.client) {
      return
    }

    currentCallback = onComplete

    Object.entries(newValues).forEach(([key, value]) => {
      const springKey = key as keyof T

      if (springs.has(springKey)) {
        springs.get(springKey)?.setTarget(value)
      }
    })

    startAnimation()
  }

  // Set function - immediately set values
  const set = (newValues: Partial<T>) => {
    Object.entries(newValues).forEach(([key, value]) => {
      const springKey = key as keyof T

      if (springs.has(springKey)) {
        const spring = springs.get(springKey)

        if (spring) {
          spring.setPosition(value)
          values[springKey] = value
        }
      }
    })
  }

  // Cleanup on component unmount
  onBeforeUnmount(() => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId)
    }
  })

  return {
    apply,
    set,
    values,
  }
}
